import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  input,
  output,
  inject,
  type ElementRef,
  viewChild,
  afterNextRender,
  effect,
} from '@angular/core';
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ZagIcon } from '../icon/icon';
import {
  SearchService,
  type SearchScopeConfig,
  type SearchQuickAction,
  type SearchResult,
  type ContextualAction,
} from '../services/search.service';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import type { BreadcrumbItem } from '../breadcrumb/breadcrumb.types';

/**
 * Top bar component for the application layout.
 * Contains breadcrumbs (left-aligned, scrollable), a collapsible search bar, and action buttons.
 *
 * Features:
 * - Breadcrumbs displayed inline with horizontal scroll and auto-scroll to end
 * - Search bar is collapsed by default, showing only a search trigger button
 * - Expanding search overlays the entire topbar with a full-width search input
 * - Auto-detects scope based on current route
 * - Scope badge is always visible (including "all" scope) for touch device accessibility
 * - Allows manual scope switching via dropdown by clicking the scope badge
 * - Shows quick actions based on search query (e.g., "Add new person")
 * - Keyboard navigation support
 */
@Component({
  selector: 'zag-top-bar',
  imports: [TranslatePipe, ZagIcon, OverlayModule, BreadcrumbComponent],
  template: `
    <header #topBarHeader class="top-bar" cdkOverlayOrigin #topBarOrigin="cdkOverlayOrigin">
      <!-- Breadcrumbs area (left-aligned, scrollable) -->
      <nav #breadcrumbContainer class="top-bar__breadcrumbs" [attr.aria-label]="'navigation.breadcrumbs' | translate">
        @if (breadcrumbs().length > 0) {
          <zag-breadcrumb [items]="breadcrumbs()" />
        }
      </nav>

      <!-- Actions area (right-aligned) -->
      <div class="top-bar__actions">
        <!-- Search trigger button -->
        <button
          type="button"
          class="top-bar__action-button top-bar__search-trigger"
          [attr.aria-label]="'common.search' | translate"
          [attr.title]="'common.search' | translate"
          (click)="expandSearch()"
        >
          <span zagIcon="search"></span>
          @if (showShortcutHint()) {
            <kbd class="top-bar__shortcut-hint">{{ shortcutKey() }}</kbd>
          }
        </button>

        <!-- AI Chat Button -->
        @if (showAIAction()) {
          <button
            type="button"
            class="top-bar__action-button top-bar__ai-button"
            [attr.aria-label]="'ai.chat' | translate"
            [attr.title]="'ai.chat' | translate"
            (click)="onAIChatClick()"
          >
            <span zagIcon="auto_awesome"></span>
          </button>
        }

        <!-- Slot directly right of the AI button (e.g. the quick-stamp button, F12) -->
        <ng-content select="[slot=after-ai]" />

        @if (showLogo()) {
          <img src="/logo.svg" alt="SmartCapacity" class="top-bar__logo" />
        } @else {
          <span zagIcon="business" class="top-bar__logo-placeholder"></span>
        }

        <ng-content select="[slot=actions]" />
      </div>
    </header>

    <!-- Search overlay as CDK overlay anchored to topbar -->
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="topBarOrigin"
      [cdkConnectedOverlayOpen]="searchExpanded()"
      [cdkConnectedOverlayHasBackdrop]="true"
      [cdkConnectedOverlayBackdropClass]="'search-overlay-backdrop'"
      [cdkConnectedOverlayPositions]="searchOverlayPositions"
      [cdkConnectedOverlayWidth]="searchOverlayWidth()"
      [cdkConnectedOverlayHeight]="searchOverlayHeight()"
      [cdkConnectedOverlayPanelClass]="'search-overlay-panel'"
      (backdropClick)="closeSearchAndDropdown()"
      (detach)="onSearchOverlayDetach()"
    >
      <div class="top-bar__search-overlay" tabindex="-1" (keydown.escape)="onOverlayEscape($event)">
        <div
          class="search-container"
          [class.search-container--focused]="searchFocused()"
          [class.search-container--open]="shouldShowDropdown()"
        >
          @if (showSearchIcon()) {
            <span zagIcon="search" class="search-container__icon"></span>
          }
          @if (searchService.scopeConfig(); as scopeConfig) {
            <button
              type="button"
              class="search-container__scope-badge"
              (click)="onScopeBadgeClick($event)"
              [attr.aria-label]="'search.changeScope' | translate"
            >
              <span [zagIcon]="scopeConfig.icon" class="search-container__scope-icon"></span>
              <span class="search-container__scope-label">{{ scopeConfig.labelKey | translate }}</span>
              <span zagIcon="expand_more" class="search-container__scope-chevron"></span>
            </button>
          }
          <input
            #searchInput
            type="text"
            class="search-container__input"
            [placeholder]="searchPlaceholder()"
            [value]="searchService.query()"
            (input)="onSearchInput($event)"
            (focus)="onSearchFocus()"
            (blur)="onSearchBlur()"
            (keydown)="onSearchKeydown($event)"
            [attr.aria-label]="'common.search' | translate"
            autocomplete="off"
          />
          @if (searchService.hasQuery()) {
            <button type="button" class="search-container__clear" (click)="onClearSearch()" [attr.aria-label]="'common.clear' | translate">
              <span zagIcon="close"></span>
            </button>
          }
          @if (showShortcutHint()) {
            <kbd class="search-container__shortcut">{{ shortcutKey() }}</kbd>
          }
        </div>

        <!-- Dropdown rendered directly below search container (not a nested CDK overlay).
             Using a nested CDK overlay caused width issues on WebKit/Safari (macOS & iOS)
             where the overlay pane didn't respect the configured width inside popovers. -->
        @if (shouldShowDropdown()) {
          <div
            class="search-dropdown"
            [style.max-height.px]="searchDropdownMaxHeight()"
            tabindex="-1"
            (keydown)="onDropdownKeydown($event)"
          >
            <!-- Quick Actions Section -->
            @if (filteredQuickActions().length > 0) {
              <div class="search-dropdown__section">
                <div class="search-dropdown__section-label">
                  {{ 'search.quickActions' | translate }}
                </div>
                @for (action of filteredQuickActions(); track action.id; let i = $index) {
                  <button
                    type="button"
                    class="search-dropdown__item"
                    [class.search-dropdown__item--focused]="focusedIndex() === i"
                    (click)="onQuickActionClick(action)"
                    (mouseenter)="focusedIndex.set(i)"
                  >
                    <span [zagIcon]="action.icon" class="search-dropdown__item-icon"></span>
                    <span class="search-dropdown__item-label">{{ action.label }}</span>
                    <span class="search-dropdown__item-hint">
                      {{ 'common.enter' | translate }}
                    </span>
                  </button>
                }
              </div>
            }

            <!-- Contextual Actions Section (query-aware navigation suggestions) -->
            @if (contextualActions().length > 0) {
              <div class="search-dropdown__section">
                <div class="search-dropdown__section-label">
                  {{ 'search.contextualActions' | translate }}
                </div>
                @for (action of contextualActions(); track action.id; let i = $index) {
                  <button
                    type="button"
                    class="search-dropdown__item"
                    [class.search-dropdown__item--focused]="focusedIndex() === getContextualActionFocusIndex(i)"
                    (click)="onContextualActionClick(action)"
                    (mouseenter)="focusedIndex.set(getContextualActionFocusIndex(i))"
                  >
                    <span [zagIcon]="action.icon" class="search-dropdown__item-icon"></span>
                    <span class="search-dropdown__item-content">
                      <span class="search-dropdown__item-label">{{ action.label }}</span>
                    </span>
                  </button>
                }
              </div>
            }

            <!-- Global Search Results Section -->
            @if (shouldShowResultsSection()) {
              <div class="search-dropdown__section">
                <div class="search-dropdown__section-label">
                  {{ 'search.results' | translate }}
                </div>
                @if (searchService.globalSearchLoading()) {
                  <div class="search-dropdown__loading">
                    <span class="search-dropdown__loading-text">{{ 'common.loading' | translate }}</span>
                  </div>
                } @else if (searchService.globalSearchResults().length === 0) {
                  <div class="search-dropdown__empty">
                    <span zagIcon="search_off" class="search-dropdown__empty-icon"></span>
                    <span class="search-dropdown__empty-text">{{ 'search.noResults' | translate }}</span>
                  </div>
                } @else {
                  @for (result of searchService.globalSearchResults(); track result.id; let i = $index) {
                    <button
                      type="button"
                      class="search-dropdown__item"
                      [class.search-dropdown__item--focused]="focusedIndex() === getResultFocusIndex(i)"
                      (click)="onResultClick(result)"
                      (mouseenter)="focusedIndex.set(getResultFocusIndex(i))"
                    >
                      <span [zagIcon]="result.icon" class="search-dropdown__item-icon"></span>
                      <span class="search-dropdown__item-content">
                        <span class="search-dropdown__item-label">{{ result.title }}</span>
                        @if (result.subtitle) {
                          <span class="search-dropdown__item-subtitle">{{ result.subtitle }}</span>
                        }
                      </span>
                    </button>
                  }
                }
              </div>
            }

            <!-- Scope Selection Section (only when explicitly opened) -->
            @if (shouldShowScopeSection()) {
              <div class="search-dropdown__section">
                <div class="search-dropdown__section-label">
                  {{ 'search.searchIn' | translate }}
                </div>
                @for (scope of searchService.allScopes(); track scope.id; let i = $index) {
                  <button
                    type="button"
                    class="search-dropdown__item"
                    [class.search-dropdown__item--active]="searchService.scope() === scope.id"
                    [class.search-dropdown__item--focused]="focusedIndex() === getScopeFocusIndex(i)"
                    (click)="onScopeSelect(scope)"
                    (mouseenter)="focusedIndex.set(getScopeFocusIndex(i))"
                  >
                    <span [zagIcon]="scope.icon" class="search-dropdown__item-icon"></span>
                    <span class="search-dropdown__item-label">{{ scope.labelKey | translate }}</span>
                    @if (searchService.scope() === scope.id) {
                      <span zagIcon="check" class="search-dropdown__item-check"></span>
                    }
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        width: 100%;
      }

      .top-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0 var(--spacing-sm);
        height: 100%;
        padding: 0;
      }

      /* Breadcrumbs area: scrollable, left-aligned, fills available space */
      .top-bar__breadcrumbs {
        flex: 1;
        min-width: 0;
        overflow-x: auto;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        display: flex;
        align-items: center;
        height: 100%;

        &::-webkit-scrollbar {
          display: none;
        }
      }

      /* Actions area: right-aligned, does not shrink */
      .top-bar__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        margin-left: auto;
        flex-shrink: 0;
      }

      .top-bar__action-button {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 36px;
        padding: 0 var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-md);
        color: var(--text-color-secondary);
        cursor: pointer;
        transition: all var(--transition-duration-fast);
        flex-shrink: 0;

        &:hover {
          background: var(--surface-color-hover);
          color: var(--color-primary);
        }

        span[zagIcon] {
          font-size: 20px;
        }
      }

      /* Search trigger: icon + optional shortcut hint */
      .top-bar__search-trigger {
        gap: var(--spacing-xs);

        span[zagIcon] {
          font-size: 24px;
        }
      }

      .top-bar__shortcut-hint {
        padding: 2px 6px;
        background: var(--surface-color-hover);
        border-radius: var(--border-radius-sm);
        font-size: 11px;
        color: var(--text-color-secondary);
        font-family: inherit;
        border: none;
        flex-shrink: 0;
      }

      @media (max-width: 767px) {
        .top-bar__shortcut-hint {
          display: none;
        }
      }

      .top-bar__ai-button {
        /* Inherits from .top-bar__action-button */
      }

      /* Hide AI button on mobile when footer is visible */
      @media (max-width: 539px) {
        .top-bar__ai-button {
          display: none;
        }
      }

      .top-bar__logo {
        height: 48px;
        width: auto;
        object-fit: contain;
      }

      .top-bar__logo-placeholder {
        font-size: 32px;
        color: var(--text-color-secondary);
      }

      /* Search overlay: rendered via CDK overlay, fills the topbar area */
      .top-bar__search-overlay {
        display: flex;
        align-items: center;
        background: var(--background-color-secondary);
        padding: 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        /* Needed so .search-dropdown can be absolutely positioned below */
        position: relative;
      }

      /* Search container (inside the overlay) */
      .search-container {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        background: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
        transition: all var(--transition-duration-fast);
        height: 100%;
        box-sizing: border-box;
        overflow: hidden;

        &:hover {
          border-color: var(--color-primary);
        }
      }

      .search-container--focused {
        border-color: var(--color-primary);
      }

      .search-container--open {
        border-color: var(--color-primary);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      .search-container__icon {
        color: var(--text-color-secondary);
        font-size: 20px;
        flex-shrink: 0;
      }

      .search-container__scope-badge {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px 2px 8px;
        background: var(--color-primary-subtle, color-mix(in srgb, var(--color-primary) 12%, transparent));
        border: 1px solid var(--color-primary-muted, color-mix(in srgb, var(--color-primary) 25%, transparent));
        border-radius: var(--border-radius-sm);
        font-size: 12px;
        color: var(--color-primary);
        font-family: inherit;
        flex-shrink: 0;
        white-space: nowrap;
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        &:hover {
          background: var(--color-primary-muted, color-mix(in srgb, var(--color-primary) 20%, transparent));
        }
      }

      .search-container__scope-icon {
        font-size: 14px;
      }

      .search-container__scope-label {
        font-weight: 500;
      }

      .search-container__scope-chevron {
        font-size: 16px;
        margin-left: -2px;
      }

      .search-container__input {
        flex: 1;
        border: none;
        background: transparent;
        color: var(--text-color-primary);
        font-size: var(--font-size-caption);
        font-family: inherit;
        outline: none;
        min-width: 0;

        &::placeholder {
          color: var(--text-color-secondary);
        }
      }

      .search-container__clear {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
        border: none;
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: all var(--transition-duration-fast);

        &:hover {
          color: var(--text-color-primary);
          background: var(--surface-color-hover);
        }

        span {
          font-size: 16px;
        }
      }

      .search-container__shortcut {
        padding: 2px 6px;
        background: var(--surface-color-hover);
        border-radius: var(--border-radius-sm);
        font-size: 11px;
        color: var(--text-color-secondary);
        font-family: inherit;
        border: none;
        flex-shrink: 0;
      }

      @media (max-width: 767px) {
        .search-container__shortcut {
          display: none;
        }
      }

      @media (max-width: 539px) {
        .search-container__scope-label {
          display: none;
        }

        .search-container__scope-badge {
          padding: 2px 4px;
        }
      }

      /* Dropdown styles - positioned directly below the search container.
         Not using a nested CDK overlay to avoid WebKit/Safari width issues
         where the overlay pane doesn't respect configured width inside popovers. */
      .search-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: var(--background-color-elevated);
        border: 1px solid var(--color-primary);
        border-top: none;
        border-bottom-left-radius: var(--border-radius-md);
        border-bottom-right-radius: var(--border-radius-md);
        box-shadow: var(--shadow-lg);
        max-height: 400px;
        overflow-y: auto;
        padding: var(--spacing-xs) 0;
        box-sizing: border-box;
        z-index: 1;
      }

      .search-dropdown__section {
        padding: var(--spacing-xs) 0;

        &:not(:last-child) {
          border-bottom: 1px solid var(--border-color-default);
        }
      }

      .search-dropdown__section-label {
        padding: var(--spacing-xs) var(--spacing-md);
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--text-color-secondary);
      }

      .search-dropdown__item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        border: none;
        background: transparent;
        font-family: inherit;
        font-size: var(--font-size-caption);
        color: var(--text-color-primary);
        cursor: pointer;
        transition: background var(--transition-duration-fast);
        text-align: left;

        &:hover,
        &.search-dropdown__item--focused {
          background: var(--surface-color-hover);
        }

        &.search-dropdown__item--active {
          color: var(--color-primary);
          font-weight: 500;
        }
      }

      .search-dropdown__item-icon {
        font-size: 18px;
        color: var(--text-color-secondary);
        flex-shrink: 0;

        .search-dropdown__item--active & {
          color: var(--color-primary);
        }
      }

      .search-dropdown__item-label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .search-dropdown__item-hint {
        font-size: 11px;
        color: var(--text-color-secondary);
        padding: 2px 6px;
        background: var(--surface-color-hover);
        border-radius: var(--border-radius-sm);
      }

      .search-dropdown__item-check {
        font-size: 18px;
        color: var(--color-primary);
      }

      .search-dropdown__loading {
        padding: var(--spacing-sm) var(--spacing-md);
        color: var(--text-color-secondary);
        font-size: var(--font-size-caption);
      }

      .search-dropdown__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-xs);
        padding: var(--spacing-lg) var(--spacing-md);
        color: var(--text-color-secondary);
      }

      .search-dropdown__empty-icon {
        font-size: 32px;
        opacity: 0.5;
      }

      .search-dropdown__empty-text {
        font-size: var(--font-size-caption);
      }

      .search-dropdown__item-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        min-width: 0;
      }

      .search-dropdown__item-subtitle {
        font-size: 11px;
        color: var(--text-color-secondary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-top-bar',
    '(document:keydown)': 'onGlobalKeydown($event)',
    '(window:resize)': 'onWindowResize()',
  },
})
export class TopBarComponent {
  /** Whether to show the logo image (set to false for storybook/testing) */
  readonly showLogo = input<boolean>(true);

  /** Whether to show the leading search icon */
  readonly showSearchIcon = input<boolean>(false);

  /** Whether to show keyboard shortcut hint */
  readonly showShortcutHint = input<boolean>(true);

  /** Whether to show the AI action button */
  readonly showAIAction = input<boolean>(true);

  /** Breadcrumb items to display in the topbar */
  readonly breadcrumbs = input<BreadcrumbItem[]>([]);

  /** Emits when AI chat button is clicked */
  readonly aiChat = output<void>();

  protected readonly searchService = inject(SearchService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  protected readonly searchFocused = signal(false);
  protected readonly focusedIndex = signal(-1);

  /** Whether the search overlay is expanded */
  readonly searchExpanded = signal(false);

  /**
   * Whether the dropdown was explicitly opened by user action (down key or shortcut).
   * On catalog pages, this is required to show the scope switcher when there's no query.
   */
  protected readonly explicitDropdownOpen = signal(false);

  /** Track the topbar dimensions for the search overlay */
  protected readonly searchOverlayWidth = signal<number | string>('auto');
  protected readonly searchOverlayHeight = signal<number | string>('auto');
  protected readonly searchDropdownMaxHeight = signal(400);

  private readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly topBarHeaderRef = viewChild<ElementRef<HTMLElement>>('topBarHeader');
  private readonly breadcrumbContainerRef = viewChild<ElementRef<HTMLElement>>('breadcrumbContainer');

  // Track language changes
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.getCurrentLang(), translations: {} },
  });

  constructor() {
    // Update dimensions after render
    afterNextRender(() => {
      this.updateSearchOverlayDimensions();
      this.updateSearchDropdownMaxHeight();
      this.scrollBreadcrumbsToEnd();
    });

    // Clear search and collapse on route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.searchExpanded()) {
          this.searchService.clear();
          this.collapseSearch();
        }
      });

    // Auto-scroll breadcrumbs to the end whenever they change
    effect(() => {
      // Subscribe to breadcrumbs changes
      this.breadcrumbs();
      // Schedule scroll after render
      setTimeout(() => {
        this.scrollBreadcrumbsToEnd();
      }, 0);
    });
  }

  /** Search overlay position: covers the topbar exactly */
  protected readonly searchOverlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
      offsetX: 0,
      offsetY: 0,
    },
  ];

  readonly shortcutKey = computed(() => {
    if (typeof navigator === 'undefined') {
      return 'Ctrl+K';
    }
    return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? '⌘K' : 'Ctrl+K';
  });

  /** Dynamic placeholder based on current scope */
  readonly searchPlaceholder = computed(() => {
    this.currentLang(); // React to language changes
    const scope = this.searchService.scopeConfig();
    if (scope.id === 'all') {
      return this.translate.instant('search.placeholderAll');
    }
    return this.translate.instant('search.placeholderScoped', {
      scope: this.translate.instant(scope.labelKey),
    });
  });

  /**
   * Quick actions derived from API scope definitions (permission-filtered by backend).
   * No longer hardcoded — actions come from the search service which fetches them from the API.
   */
  protected readonly quickActions = computed<SearchQuickAction[]>(() => {
    this.currentLang(); // React to language changes (triggers re-translation)
    return this.searchService.quickActions();
  });

  /**
   * Quick actions filtered by current search query and scope.
   * Only shows actions relevant to the current scope.
   */
  protected readonly filteredQuickActions = computed<SearchQuickAction[]>(() => {
    const query = this.searchService.query().toLowerCase().trim();
    if (!query) return [];

    const actions = this.quickActions();
    const currentScope = this.searchService.scope();

    return actions.filter(action => {
      // First check if action is available in current scope
      if (!action.scopes.includes(currentScope)) {
        return false;
      }
      // Then check if query matches keywords or label
      return action.keywords.some(keyword => keyword.toLowerCase().includes(query)) || action.label.toLowerCase().includes(query);
    });
  });

  /**
   * Contextual actions with pre-translated scope labels and final message.
   * The backend sends `labelParams.scope` as a translation key (e.g. 'control.environments').
   * We translate both the scope name and the final message here.
   */
  protected readonly contextualActions = computed<Array<ContextualAction & { label: string }>>(() => {
    this.currentLang(); // React to language changes
    return this.searchService.contextualActions().map(action => {
      // Translate scope labelKey to the actual scope name
      const translatedScope = action.labelParams['scope']
        ? (this.translate.instant(action.labelParams['scope']) as string)
        : (action.labelParams['scope'] ?? '');

      // Create interpolation params with pre-translated scope and other params
      const params = {
        ...action.labelParams,
        scope: translatedScope,
      };

      // Pre-translate the entire message to get the final text
      const label = this.translate.instant(action.labelKey, params);

      return {
        ...action,
        label, // Add pre-translated label
      };
    });
  });

  /**
   * Whether the dropdown should actually be visible.
   * Rules:
   * - Show if there are quick actions matching
   * - Show if there are contextual actions
   * - Show if there are global search results
   * - Show if a global search was performed (to show "no results")
   * - Show if explicitly opened (down key/shortcut)
   * - Never show if isOpen is false
   */
  protected readonly shouldShowDropdown = computed(() => {
    if (!this.searchService.isOpen()) return false;

    const hasQuickActions = this.filteredQuickActions().length > 0;
    const hasContextualActions = this.contextualActions().length > 0;
    const hasGlobalResults = this.searchService.globalSearchResults().length > 0;
    const isLoading = this.searchService.globalSearchLoading();
    const searchPerformed = this.searchService.globalSearchPerformed();
    const explicitlyOpened = this.explicitDropdownOpen();

    // Show dropdown if there are results, loading, search was performed, or explicitly opened
    return hasQuickActions || hasContextualActions || hasGlobalResults || isLoading || searchPerformed || explicitlyOpened;
  });

  /**
   * Whether to show the scope selection section in the dropdown.
   * Only show when explicitly opened (via down key or shortcut).
   */
  protected readonly shouldShowScopeSection = computed(() => {
    return this.explicitDropdownOpen();
  });

  /**
   * Whether to show the global search results section.
   * Shows when loading, has results, or a search was performed with no results.
   * Only hidden when on an actual catalog page WITHOUT a manual scope override
   * (because the catalog page itself handles filtering).
   * Also suppressed when contextual actions are present and there are no entity results —
   * the suggestions section already provides useful navigation in that case.
   */
  protected readonly shouldShowResultsSection = computed(() => {
    // On a catalog page with auto-detected scope, the page handles filtering
    if (this.searchService.isCatalogPage() && !this.searchService.hasManualScope()) {
      return false;
    }

    const hasResults = this.searchService.globalSearchResults().length > 0;
    const isLoading = this.searchService.globalSearchLoading();
    const searchPerformed = this.searchService.globalSearchPerformed();

    // Always show when loading or when there are actual results
    if (isLoading || hasResults) return true;

    // Suppress the "no results" empty state when contextual actions are present —
    // the user has already found useful suggestions and the empty panel adds noise.
    if (searchPerformed && this.contextualActions().length > 0) return false;

    return searchPerformed;
  });

  onGlobalKeydown(event: KeyboardEvent): void {
    // Handle Cmd/Ctrl + K to toggle search
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      event.stopPropagation();
      if (this.searchExpanded()) {
        this.collapseSearch();
      } else {
        this.expandSearch();
      }
    }
  }

  onWindowResize(): void {
    this.updateSearchOverlayDimensions();
    this.updateSearchDropdownMaxHeight();
  }

  /** Expand the search overlay and focus the input */
  expandSearch(): void {
    this.updateSearchOverlayDimensions();
    this.updateSearchDropdownMaxHeight();
    this.searchExpanded.set(true);
    // Don't show scope section on initial expand - just focus the input.
    // User can press arrow down or click scope badge to see scope options.
    // this.explicitDropdownOpen.set(true);
    this.searchService.open();

    // Focus after the overlay is attached. Safari can miss first attempt.
    this.focusSearchInputWithRetry();
  }

  /** Collapse the search overlay */
  collapseSearch(): void {
    this.searchExpanded.set(false);
    this.searchService.close();
    this.explicitDropdownOpen.set(false);
  }

  /** Called when the search overlay detaches (e.g. backdrop click) */
  protected onSearchOverlayDetach(): void {
    this.collapseSearch();
  }

  /** Handle escape key on the overlay */
  protected onOverlayEscape(event: Event): void {
    event.stopPropagation();
    if (this.shouldShowDropdown()) {
      // Dropdown is visible: close it but keep search bar expanded
      this.closeDropdown();
    } else {
      // No dropdown visible: collapse the entire search bar
      this.collapseSearch();
    }
  }

  /** Close search and dropdown together (e.g., when clicking the backdrop) */
  protected closeSearchAndDropdown(): void {
    this.closeDropdown();
    this.collapseSearch();
  }

  /** Update search overlay dimensions to match topbar header */
  private updateSearchOverlayDimensions(): void {
    const header = this.topBarHeaderRef()?.nativeElement;
    if (header) {
      this.searchOverlayWidth.set(header.offsetWidth);
      this.searchOverlayHeight.set(header.offsetHeight);
    }
  }

  /** Keep dropdown height within visible viewport (especially on mobile Safari). */
  private updateSearchDropdownMaxHeight(): void {
    const header = this.topBarHeaderRef()?.nativeElement;
    if (!header || globalThis.window === undefined) {
      return;
    }

    const viewportHeight = globalThis.visualViewport?.height ?? globalThis.innerHeight;
    const availableHeight = Math.floor(viewportHeight - header.getBoundingClientRect().bottom - 12);
    this.searchDropdownMaxHeight.set(Math.max(120, availableHeight));
  }

  /** Focus input robustly across browsers (Safari may need retries). */
  private focusSearchInputWithRetry(attempt = 0): void {
    const input = this.searchInputRef()?.nativeElement;
    if (!input) {
      if (attempt < 6 && globalThis.window !== undefined) {
        globalThis.requestAnimationFrame(() => this.focusSearchInputWithRetry(attempt + 1));
      }
      return;
    }

    input.focus({ preventScroll: true });
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition);

    if (document !== undefined && document.activeElement !== input && attempt < 6 && globalThis.window !== undefined) {
      globalThis.requestAnimationFrame(() => this.focusSearchInputWithRetry(attempt + 1));
    }
  }

  /** Auto-scroll the breadcrumb container to the end (rightmost element) */
  private scrollBreadcrumbsToEnd(): void {
    const container = this.breadcrumbContainerRef()?.nativeElement;
    if (container) {
      container.scrollLeft = container.scrollWidth;
    }
  }

  protected onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchService.setQuery(target.value);

    // Reset explicit open state when typing
    this.explicitDropdownOpen.set(false);

    // Open dropdown when typing (will only show if quick actions match)
    if (target.value.trim()) {
      this.searchService.open();
      this.focusedIndex.set(-1);
    } else {
      // Close when clearing the input
      this.searchService.close();
    }
  }

  protected onSearchFocus(): void {
    this.searchFocused.set(true);
  }

  protected onSearchBlur(): void {
    this.searchFocused.set(false);
    // No need to collapse on blur — the CDK overlay backdrop handles dismissal.
    // The search overlay lives in a separate DOM tree, so clicking scope badge,
    // clear button, or other elements inside the overlay won't cause blur issues.
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation(); // Prevent double-handling by parent overlay div
      if (this.searchService.isOpen()) {
        // First Escape: close dropdown but keep search expanded with query intact
        this.closeDropdown();
      } else {
        // Second Escape: collapse search bar (keeps query for when re-opened)
        this.collapseSearch();
      }
    } else if (event.key === 'Backspace') {
      // Reset to auto-detected scope when pressing backspace with empty input and manual scope active
      if (!this.searchService.hasQuery() && this.searchService.hasManualScope()) {
        event.preventDefault();
        this.searchService.resetToAutoScope();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Explicitly open dropdown with scope section
      this.explicitDropdownOpen.set(true);
      this.searchService.open();
      this.navigateDropdown(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.searchService.isOpen()) {
        this.navigateDropdown(-1);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.handleEnter();
    }
  }

  protected onDropdownKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeDropdown();
      this.searchInputRef()?.nativeElement.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.navigateDropdown(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.navigateDropdown(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.handleEnter();
    }
  }

  protected onScopeBadgeClick(event: Event): void {
    event.stopPropagation();
    // Clicking scope badge explicitly opens with scope section
    this.explicitDropdownOpen.set(true);
    this.searchService.toggle();
  }

  protected onClearSearch(): void {
    this.searchService.clear();
    this.closeDropdown();
    this.searchInputRef()?.nativeElement.focus();
  }

  protected onQuickActionClick(action: SearchQuickAction): void {
    const navigation = this.searchService.resolveQuickActionNavigation(action);
    if (!navigation) {
      return;
    }

    this.closeDropdown();
    this.searchService.clear();
    this.collapseSearch();
    this.router.navigate([navigation.route], { queryParams: navigation.queryParams });
  }

  protected onScopeSelect(scope: SearchScopeConfig): void {
    // Just select the scope, don't navigate
    this.searchService.selectScope(scope.id);
    this.explicitDropdownOpen.set(false);
    // Refocus the input so the user can keep typing
    this.searchInputRef()?.nativeElement.focus();
  }

  protected onContextualActionClick(action: ContextualAction): void {
    this.closeDropdown();
    this.searchService.clear();
    this.collapseSearch();
    this.router.navigate([action.route], { queryParams: action.queryParams });
  }

  protected onResultClick(result: SearchResult): void {
    this.closeDropdown();
    this.searchService.clear();
    this.collapseSearch();
    // Use Angular router for navigation, passing optional query params for shareable URLs
    this.router.navigate([result.route], {
      queryParams: result.routeQueryParams,
    });
  }

  /**
   * Get the focus index for a contextual action item.
   * Contextual actions appear after quick actions.
   */
  protected getContextualActionFocusIndex(actionIndex: number): number {
    return this.filteredQuickActions().length + actionIndex;
  }

  /**
   * Get the focus index for a search result item.
   * Results appear after quick actions and contextual actions.
   */
  protected getResultFocusIndex(resultIndex: number): number {
    return this.filteredQuickActions().length + this.contextualActions().length + resultIndex;
  }

  /**
   * Get the focus index for a scope selection item.
   */
  protected getScopeFocusIndex(scopeIndex: number): number {
    const quickActionsCount = this.filteredQuickActions().length;
    const contextualActionsCount = this.contextualActions().length;
    const resultsCount = this.shouldShowResultsSection() ? this.searchService.globalSearchResults().length : 0;
    return quickActionsCount + contextualActionsCount + resultsCount + scopeIndex;
  }

  protected closeDropdown(): void {
    this.searchService.close();
    this.explicitDropdownOpen.set(false);
  }

  private navigateDropdown(direction: number): void {
    const quickActionsCount = this.filteredQuickActions().length;
    const contextualActionsCount = this.contextualActions().length;
    const resultsCount = this.shouldShowResultsSection() ? this.searchService.globalSearchResults().length : 0;
    const scopesCount = this.shouldShowScopeSection() ? this.searchService.allScopes().length : 0;
    const totalItems = quickActionsCount + contextualActionsCount + resultsCount + scopesCount;

    if (totalItems === 0) return;

    const current = this.focusedIndex();
    let next = current + direction;

    if (next < 0) next = totalItems - 1;
    if (next >= totalItems) next = 0;

    this.focusedIndex.set(next);
  }

  private handleEnter(): void {
    const index = this.focusedIndex();
    const quickActions = this.filteredQuickActions();
    const contextualActions = this.contextualActions();
    const results = this.searchService.globalSearchResults();
    const resultsCount = this.shouldShowResultsSection() ? results.length : 0;

    const dispatched = this.dispatchFocusedItem(index, quickActions, contextualActions, results, resultsCount);
    if (!dispatched) {
      this.searchService.search();
      this.closeDropdown();
    }
  }

  /**
   * Dispatch the Enter key action for the currently focused item.
   * Returns true if an item was activated, false if nothing was focused.
   */
  private dispatchFocusedItem(
    index: number,
    quickActions: SearchQuickAction[],
    contextualActions: ContextualAction[],
    results: SearchResult[],
    resultsCount: number
  ): boolean {
    const qCount = quickActions.length;
    const caCount = contextualActions.length;

    if (index >= 0 && index < qCount) {
      this.onQuickActionClick(quickActions[index]);
      return true;
    }
    if (index >= qCount && index < qCount + caCount) {
      const action = contextualActions[index - qCount];
      if (action) this.onContextualActionClick(action);
      return true;
    }
    if (index >= qCount + caCount && index < qCount + caCount + resultsCount) {
      const result = results[index - qCount - caCount];
      if (result) this.onResultClick(result);
      return true;
    }
    if (this.shouldShowScopeSection() && index >= qCount + caCount + resultsCount) {
      const scope = this.searchService.allScopes()[index - qCount - caCount - resultsCount];
      if (scope) this.onScopeSelect(scope);
      return true;
    }
    return false;
  }

  protected onAIChatClick(): void {
    this.aiChat.emit();
  }
}
